import { type Mesh, MeshBuilder, type Scene, TransformNode } from "@babylonjs/core";
import { mat, solid } from "./materials";

/** The ruin base top sits this far above the carved plaza floor, so the terrain never shows through. */
export const BASE_LIFT = 0.02;
const COLUMN_ANGLES = [135, 225, 315]; // degrees counter-clockwise from +x; 90° (north, the door) stays clear
const COLUMN_HEIGHTS = [4, 5, 6];

/**
 * Sunken circular plaza around the nomen stone with three outward-leaning column stumps (spec 2d §2.1).
 * groundY is the plaza floor: world.json carves the terrain 0.3 m down within the radius.
 */
export function buildFoundationRuin(scene: Scene, pos: [number, number], radius: number, groundY: number): Mesh[] {
  const concrete = mat(scene, "concrete", 0.62, 0.56, 0.5);
  const rust = mat(scene, "rust", 0.55, 0.32, 0.2);
  const root = new TransformNode("ruin", scene);
  root.position.set(pos[0], groundY, pos[1]);
  const out: Mesh[] = [];
  const base = solid(MeshBuilder.CreateCylinder("ruin:base", { diameter: radius * 2, height: 1.5, tessellation: 48 }, scene), concrete);
  base.position.y = BASE_LIFT - 0.75;
  base.parent = root;
  out.push(base);
  COLUMN_ANGLES.forEach((deg, i) => {
    const ang = (deg * Math.PI) / 180, h = COLUMN_HEIGHTS[i]!;
    const col = solid(MeshBuilder.CreateCylinder(`ruin:col${i}`, { diameter: 1.2, height: h, tessellation: 12 }, scene), rust);
    col.parent = root;
    const r = radius * 0.7;
    col.position.set(Math.cos(ang) * r, BASE_LIFT + h / 2, Math.sin(ang) * r);
    const tilt = (12 * Math.PI) / 180;
    col.rotation.set(Math.sin(ang) * tilt, 0, -Math.cos(ang) * tilt);
    out.push(col);
  });
  let seed = 7;
  const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  for (let i = 0; i < 7; i++) {
    // rubble on the rim, skipping the northern quarter (the walk to the door)
    const ang = ((150 + rnd() * 240) * Math.PI) / 180, r = radius - 0.5 + rnd() * 2;
    const w = 0.8 + rnd() * 1.2, h = 0.3 + rnd() * 0.5, d = 0.8 + rnd() * 1.2;
    const box = solid(MeshBuilder.CreateBox(`ruin:rubble${i}`, { width: w, height: h, depth: d }, scene), concrete);
    box.parent = root;
    box.position.set(Math.cos(ang) * r, BASE_LIFT + h / 2, Math.sin(ang) * r);
    box.rotation.set(rnd() * 0.3, rnd() * Math.PI, rnd() * 0.3);
    out.push(box);
  }
  return out;
}
